import api from "@/lib/axiosConfig";
import i18n, { type SupportedLocale } from "@/i18n";
import { visibleFallback } from "@/lib/cms/cms-new-i18n";
import { BASE, type PuckPageApi, toRecord } from "@/api/cms-puck/client";
import { createPuckPage } from "@/api/cms-puck/pages";
import type {
  CatalogFlowBinding,
  CatalogFlowStep,
  CatalogFlowTarget,
  MemberFlowBinding,
  MemberFlowStep,
  PuckPageRecord,
  SearchFlowBinding,
  SearchFlowStep,
  SearchFlowTarget,
  ShellFlowBinding,
  ShellFlowStep,
} from "@/api/cms-puck/types";

/** Message d'erreur partagé quand un template de parcours existe déjà. */
function templateExistsError(pageName: string): Error {
  return new Error(
    i18n.t("bo.cms.api.templateExists", {
      pageName,
      defaultValue: visibleFallback(
        "bo.cms.api.templateExists",
        "Un template existe déjà ({{pageName}}). Éditez-le ou réassignez une autre page via la liste.",
      ),
    }),
  );
}

/* -------------------------------------------------------------------------- */
/* Parcours catalogue (modèles Puck par étape / cible)                        */
/* -------------------------------------------------------------------------- */

export async function getCatalogFlow(): Promise<CatalogFlowBinding[]> {
  const { data } = await api.get<CatalogFlowBinding[]>(`${BASE}/catalog-flow`);
  return data;
}

export async function upsertCatalogFlow(input: {
  target: CatalogFlowTarget;
  step: CatalogFlowStep;
  pageId: string | null;
}): Promise<CatalogFlowBinding[]> {
  const { data } = await api.put<CatalogFlowBinding[]>(
    `${BASE}/catalog-flow`,
    input,
  );
  return data;
}

/** Crée une page template catalogue pré-remplie et l'assigne au parcours. */
export async function createCatalogFlowTemplate(input: {
  target: CatalogFlowTarget;
  step: CatalogFlowStep;
}): Promise<{ page: PuckPageRecord; bindings: CatalogFlowBinding[] }> {
  const bindings = await getCatalogFlow();
  const existing = bindings.find(
    (b) => b.target === input.target && b.step === input.step,
  );
  if (existing) throw templateExistsError(existing.pageName);

  const { getCatalogFlowTemplateName, getCatalogFlowTemplateSeed } = await import(
    "@/lib/cms-puck/templates/catalog-flow-templates"
  );
  const page = await createPuckPage({
    name: getCatalogFlowTemplateName(input.target, input.step),
    data: getCatalogFlowTemplateSeed(input.step, input.target),
  });
  const updatedBindings = await upsertCatalogFlow({
    target: input.target,
    step: input.step,
    pageId: page.id,
  });
  return { page, bindings: updatedBindings };
}

/* -------------------------------------------------------------------------- */
/* Parcours membre (connexion / inscription / profil / favoris)               */
/* -------------------------------------------------------------------------- */

export async function getMemberFlow(): Promise<MemberFlowBinding[]> {
  const { data } = await api.get<MemberFlowBinding[]>(`${BASE}/member-flow`);
  return data;
}

export async function upsertMemberFlow(input: {
  step: MemberFlowStep;
  pageId: string | null;
}): Promise<MemberFlowBinding[]> {
  const { data } = await api.put<MemberFlowBinding[]>(
    `${BASE}/member-flow`,
    input,
  );
  return data;
}

/** Crée une page template membre pré-remplie et l'assigne au parcours. */
export async function createMemberFlowTemplate(input: {
  step: MemberFlowStep;
}): Promise<{ page: PuckPageRecord; bindings: MemberFlowBinding[] }> {
  const bindings = await getMemberFlow();
  const existing = bindings.find((b) => b.step === input.step);
  if (existing) throw templateExistsError(existing.pageName);

  const { getMemberFlowTemplateName, getMemberFlowTemplateSeed } = await import(
    "@/lib/cms-puck/templates/member-flow-templates"
  );
  const page = await createPuckPage({
    name: getMemberFlowTemplateName(input.step),
    data: getMemberFlowTemplateSeed(input.step),
  });
  const updatedBindings = await upsertMemberFlow({
    step: input.step,
    pageId: page.id,
  });
  return { page, bindings: updatedBindings };
}

/* -------------------------------------------------------------------------- */
/* Maintenance (page CMS avancé dédiée)                                              */
/* -------------------------------------------------------------------------- */

export async function createMaintenanceTemplate(): Promise<{
  page: PuckPageRecord;
  maintenancePageId: string;
}> {
  const { data } = await api.post<{
    page: PuckPageApi;
    maintenancePageId: string;
  }>(`${BASE}/maintenance/template`);
  return { page: toRecord(data.page), maintenancePageId: data.maintenancePageId };
}

/* -------------------------------------------------------------------------- */
/* Abonnement (page CMS avancé route /abonnement)                                    */
/* -------------------------------------------------------------------------- */

export async function createAbonnementTemplate(): Promise<{
  page: PuckPageRecord;
  abonnementPageId: string;
}> {
  const { data } = await api.post<{
    page: PuckPageApi;
    abonnementPageId: string;
  }>(`${BASE}/abonnement/template`);
  return { page: toRecord(data.page), abonnementPageId: data.abonnementPageId };
}

/** Crée le modèle Puck « fiche rubrique » Sélection et l'assigne au CMS. */
export async function createSelectionCategoryDetailTemplate(): Promise<{
  page: PuckPageRecord;
  selectionCategoryDetailPageId: string;
}> {
  const { data } = await api.post<{
    page: PuckPageApi;
    selectionCategoryDetailPageId: string;
  }>(`${BASE}/selection-category-detail/template`);
  return {
    page: toRecord(data.page),
    selectionCategoryDetailPageId: data.selectionCategoryDetailPageId,
  };
}

/* -------------------------------------------------------------------------- */
/* Parcours recherche (résultats catalogue)                                    */
/* -------------------------------------------------------------------------- */

export async function getSearchFlow(): Promise<SearchFlowBinding[]> {
  const { data } = await api.get<SearchFlowBinding[]>(`${BASE}/search-flow`);
  return data;
}

export async function upsertSearchFlow(input: {
  target: SearchFlowTarget;
  step: SearchFlowStep;
  pageId: string | null;
}): Promise<SearchFlowBinding[]> {
  const { data } = await api.put<SearchFlowBinding[]>(
    `${BASE}/search-flow`,
    input,
  );
  return data;
}

export async function createSearchFlowTemplate(input: {
  target: SearchFlowTarget;
  step: SearchFlowStep;
}): Promise<{ page: PuckPageRecord; bindings: SearchFlowBinding[] }> {
  const bindings = await getSearchFlow();
  const existing = bindings.find(
    (b) => b.target === input.target && b.step === input.step,
  );
  if (existing) throw templateExistsError(existing.pageName);

  const { getSearchFlowTemplateName, getSearchFlowTemplateSeed } = await import(
    "@/lib/cms-puck/templates/search-flow-templates"
  );
  const page = await createPuckPage({
    name: getSearchFlowTemplateName(input.target),
    data: getSearchFlowTemplateSeed(input.target),
  });
  const updatedBindings = await upsertSearchFlow({
    target: input.target,
    step: input.step,
    pageId: page.id,
  });
  return { page, bindings: updatedBindings };
}

/* -------------------------------------------------------------------------- */
/* En-tête & pied de page (chrome global Puck)                                 */
/* -------------------------------------------------------------------------- */

export async function getShellFlow(): Promise<ShellFlowBinding[]> {
  const { data } = await api.get<ShellFlowBinding[]>(`${BASE}/shell-flow`);
  return data;
}

export async function upsertShellFlow(input: {
  step: ShellFlowStep;
  pageId: string | null;
}): Promise<ShellFlowBinding[]> {
  const { data } = await api.put<ShellFlowBinding[]>(
    `${BASE}/shell-flow`,
    input,
  );
  return data;
}

/** Espace réservé sous l'en-tête Puck (réglage du site, sans republier). */
export async function setShellHeaderSpacing(
  spacing: "classic" | "fit",
): Promise<ShellFlowBinding[]> {
  const { data } = await api.put<ShellFlowBinding[]>(
    `${BASE}/shell-flow/header-spacing`,
    { spacing },
  );
  return data;
}

/** Crée une page template shell pré-remplie et l'assigne à la zone. */
export async function createShellFlowTemplate(input: {
  step: ShellFlowStep;
  /** Langue des textes du modèle (titre, boutons) ; défaut = langue du site. */
  lang?: SupportedLocale;
}): Promise<{ page: PuckPageRecord; bindings: ShellFlowBinding[] }> {
  const bindings = await getShellFlow();
  const existing = bindings.find((b) => b.step === input.step);
  if (existing) throw templateExistsError(existing.pageName);

  const { getShellFlowTemplateName, getShellFlowTemplateSeed } = await import(
    "@/lib/cms-puck/templates/shell-flow-templates"
  );
  const page = await createPuckPage({
    name: getShellFlowTemplateName(input.step),
    data: getShellFlowTemplateSeed(input.step),
    lang: input.lang,
  });
  const updatedBindings = await upsertShellFlow({
    step: input.step,
    pageId: page.id,
  });
  return { page, bindings: updatedBindings };
}
